import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ScopeSwitcher } from '../components/ScopeSwitcher';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { useScope } from '../lib/hooks';
import { productsApi, aggregatesApi, categoriesApi, listsApi, formatShoppingDate, type ShoppingListSummary, type Category, type ProductSummary, type AggregateSummary, type ExternalProduct } from '../lib/domain';
import { ApiError, OfflineError } from '../lib/api';

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof OfflineError) return err.message;
  return 'Aktion fehlgeschlagen. Bitte erneut versuchen.';
}

// Vorschau über den eigenen Server: der Browser lädt nichts direkt von OpenFoodFacts.
function ExternalImage({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    productsApi.externalImage(url).then((r) => !cancelled && setSrc(r.dataUrl)).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [url]);
  return src ? <img src={src} alt="Produktbild" style={{ height: 80, display: 'block' }} /> : <div style={{ height: 80, width: 80 }} aria-busy="true" />;
}

const EAN_PATTERN = /^\d{8,14}$/;

export function ProductsPage() {
  const [scope] = useScope();
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [aggregates, setAggregates] = useState<AggregateSummary[]>([]);
  const [newAggregateName, setNewAggregateName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [external, setExternal] = useState<ExternalProduct | null>(null);
  const [useImages, setUseImages] = useState(true);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [createMode, setCreateMode] = useState<'manual' | 'ean'>('manual');
  const [newName, setNewName] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [newCategoryId, setNewCategoryId] = useState('');
  const [newEan, setNewEan] = useState('');
  const [newAggregateId, setNewAggregateId] = useState('');
  const [externalName, setExternalName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openLists, setOpenLists] = useState<ShoppingListSummary[]>([]);
  const [targetListId, setTargetListId] = useState('');
  const [moving, setMoving] = useState(false);
  const [dayDate, setDayDate] = useState(() => new Date().toISOString().slice(0, 10));

  const eanQuery = EAN_PATTERN.test(newEan.trim()) ? newEan.trim() : null;
  const groupId = scope?.startsWith('group:') ? scope.slice(6) : undefined;
  const categoryNames = new Map(categories.map((c) => [c.id, c.name]));

  useEffect(() => {
    setExternal(null);
    setNotice(null);
  }, [newEan, scope]);

  useEffect(() => {
    if (!scope) return;
    setError(null);
    categoriesApi.list(scope).then((r) => setCategories(r.categories)).catch(() => setCategories([]));
    productsApi.list(scope, query ? { query } : {}).then((r) => setProducts(r.products)).catch((err) => setError(errorMessage(err)));
    aggregatesApi.list(scope, query || undefined).then((r) => setAggregates(r.aggregates)).catch((err) => setError(errorMessage(err)));
  }, [scope, query]);

  // Aktive (offene) Listen inkl. Merkliste als Ziel für markierte Produkte.
  useEffect(() => {
    if (!scope) return;
    setSelected(new Set());
    listsApi
      .list(scope, 'OPEN')
      .then((r) => {
        setOpenLists(r.data.lists);
        setTargetListId((cur) => (r.data.lists.some((l) => l.id === cur) ? cur : r.data.lists[0]?.id ?? ''));
      })
      .catch(() => setOpenLists([]));
  }, [scope]);

  function toggleSelected(productId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }

  // Markierte Produkte (Menge 1 Stück; vorhandene Positionen werden
  // zusammengeführt) auf die gewählte Liste setzen und die Markierung lösen.
  async function addSelectedToList() {
    if (!targetListId || selected.size === 0) return;
    setError(null);
    setNotice(null);
    setMoving(true);
    const failed = new Set<string>();
    const reasons: string[] = [];
    for (const product of products.filter((p) => selected.has(p.id))) {
      try {
        await listsApi.addItem(targetListId, { productId: product.id, targetQuantity: 1, unit: 'STUECK' }, product);
      } catch (err) {
        failed.add(product.id);
        reasons.push(errorMessage(err));
      }
    }
    const added = selected.size - failed.size;
    setSelected(failed);
    const target = openLists.find((l) => l.id === targetListId);
    setNotice(`${added} Produkt(e) zu „${target?.memoScopeKey ? 'Merkliste' : target?.name || 'Ohne Namen'}“ hinzugefügt.`);
    if (failed.size > 0) setError(`${failed.size} Produkt(e) konnten nicht hinzugefügt werden. ${reasons.join(' ')}`);
    setMoving(false);
  }

  // Markierte Produkte einem Einkaufstag zuordnen; sie werden nach Standardgeschäft
  // auf die Listen des Tages verteilt, der Rest bleibt zur manuellen Zuordnung offen.
  async function addSelectedToDay() {
    if (!scope || !dayDate || selected.size === 0) return;
    setError(null);
    setNotice(null);
    setMoving(true);
    try {
      const { day } = await listsApi.addDayProducts(scope, dayDate, [...selected].map((productId) => ({ productId })));
      const open = day.products.filter((p) => !p.listId).length;
      setNotice(`${selected.size} Produkt(e) dem ${formatShoppingDate(dayDate)} zugeordnet.${open > 0 ? ` ${open} noch ohne Liste.` : ''}`);
      setSelected(new Set());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setMoving(false);
    }
  }

  async function createManual() {
    if (!scope || !newName.trim()) return;
    setError(null);
    try {
      await productsApi.quickCreate(scope, newName.trim(), groupId, {
        brand: newBrand.trim() || undefined,
        categoryId: newCategoryId || undefined,
        aggregateId: newAggregateId || undefined,
      });
      setNewName('');
      setNewBrand('');
      setNotice('Produkt angelegt.');
      const r = await aggregatesApi.list(scope);
      setAggregates(r.aggregates);
      setProducts((await productsApi.list(scope, query ? { query } : {})).products);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  // Statt ein Produkt mit der EAN als Namen anzulegen: erst bei OpenFoodFacts suchen.
  async function searchOpenFoodFacts(ean: string | null = eanQuery) {
    if (!scope || !ean) return;
    setError(null);
    setNotice(null);
    setLookupBusy(true);
    try {
      const r = await productsApi.lookupByEan(scope, ean);
      if (r.found === 'local' && r.product) {
        setNotice(`Diese EAN gehört bereits zu „${r.product.name}“.`);
      } else if (r.found === 'external' && r.external) {
        setExternal(r.external);
        setExternalName(r.external.name ?? '');
        setSelectedImages(r.external.images.slice(0, 5));
        setUseImages(r.external.images.length > 0);
      } else {
        setNotice('Bei OpenFoodFacts wurde zu dieser EAN nichts gefunden.');
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLookupBusy(false);
    }
  }

  async function createFromExternal() {
    if (!scope || !eanQuery) return;
    setError(null);
    try {
      const r = await productsApi.resolveByEan(eanQuery, groupId, useImages ? selectedImages : [], newCategoryId || undefined, {
        name: externalName.trim() || undefined,
        aggregateId: newAggregateId || undefined,
      });
      if (r.found === 'none') {
        setNotice('Produkt konnte nicht angelegt werden.');
        return;
      }
      setExternal(null);
      setNewEan('');
      setExternalName('');
      setNotice(`Produkt „${r.product?.name ?? ''}“ angelegt.`);
      aggregatesApi.list(scope).then((a) => setAggregates(a.aggregates)).catch(() => {});
      setProducts((await productsApi.list(scope, query ? { query } : {})).products);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  function toggleImage(url: string) {
    setSelectedImages((cur) => (cur.includes(url) ? cur.filter((u) => u !== url) : [...cur, url]));
  }

  async function createAggregate() {
    if (!scope || !newAggregateName.trim()) return;
    setError(null);
    try {
      await aggregatesApi.create(newAggregateName.trim(), scope.startsWith('group:') ? scope.slice(6) : undefined);
      setNewAggregateName('');
      const r = await aggregatesApi.list(scope);
      setAggregates(r.aggregates);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <div className="stack">
      <h1>Produkte & Sammelprodukte</h1>
      <ScopeSwitcher />
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}

      <div className="field">
        <label htmlFor="product-query">Suchen</label>
        <input id="product-query" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, Marke oder EAN" />
      </div>

      <section className="card stack" aria-label="Neues Produkt">
        <h2>Neues Produkt</h2>
        <div className="row">
          <label className="row">
            <input type="radio" name="create-mode" checked={createMode === 'manual'} onChange={() => setCreateMode('manual')} />
            Manuell
          </label>
          <label className="row">
            <input type="radio" name="create-mode" checked={createMode === 'ean'} onChange={() => setCreateMode('ean')} />
            Per EAN (OpenFoodFacts)
          </label>
        </div>
        {createMode === 'manual' && (
          <>
            <div className="field">
              <label htmlFor="new-product-name">Name</label>
              <input id="new-product-name" value={newName} onChange={(e) => setNewName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="new-product-brand">Marke (optional)</label>
              <input id="new-product-brand" value={newBrand} onChange={(e) => setNewBrand(e.target.value)} />
            </div>
          </>
        )}
        {createMode === 'ean' && (
          <div className="field">
            <label htmlFor="new-product-ean">EAN</label>
            <input id="new-product-ean" inputMode="numeric" value={newEan} onChange={(e) => setNewEan(e.target.value)} placeholder="8–14 Ziffern" />
          </div>
        )}
        <div className="field">
          <label htmlFor="new-product-category">Kategorie (optional)</label>
          <select id="new-product-category" value={newCategoryId} onChange={(e) => setNewCategoryId(e.target.value)}>
            <option value="">– keine –</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="new-product-aggregate">Sammelprodukt (optional)</label>
          <select id="new-product-aggregate" value={newAggregateId} onChange={(e) => setNewAggregateId(e.target.value)}>
            <option value="">– keines –</option>
            {aggregates.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
        {createMode === 'manual' && (
          <button onClick={createManual} disabled={!newName.trim()}>Produkt anlegen</button>
        )}
        {createMode === 'ean' && (
          <button onClick={() => void searchOpenFoodFacts()} disabled={lookupBusy || !eanQuery}>
            🔎 Bei OpenFoodFacts suchen
          </button>
        )}
        {createMode === 'ean' && (
          <BarcodeScanner
            label="📷 EAN scannen"
            disabled={lookupBusy}
            onDetect={async (code) => {
              setNewEan(code);
              await searchOpenFoodFacts(code);
            }}
            onError={setError}
          />
        )}
        {notice && <p className="muted" role="status">{notice}</p>}
        {createMode === 'ean' && external && (
          <div className="card stack" aria-label="OpenFoodFacts-Treffer">
            <div className="field">
              <label htmlFor="external-name">Name</label>
              <input id="external-name" value={externalName} onChange={(e) => setExternalName(e.target.value)} placeholder="Produktname eingeben" />
              {!external.name && <span className="muted">OpenFoodFacts liefert zu dieser EAN keinen Namen – bitte selbst eintragen.</span>}
            </div>
            {external.brand && <div className="muted">{external.brand}</div>}
            {external.netQuantity && <div className="muted">{external.netQuantity}</div>}
            <div className="muted">EAN {external.ean}</div>
            {external.images.length > 0 && (
              <>
                <label className="row">
                  <input type="checkbox" checked={useImages} onChange={(e) => setUseImages(e.target.checked)} />
                  Bilder von OpenFoodFacts übernehmen
                </label>
                {useImages && (
                  <div className="row" style={{ flexWrap: 'wrap' }}>
                    {external.images.slice(0, 5).map((url) => (
                      <label key={url}>
                        <input type="checkbox" checked={selectedImages.includes(url)} onChange={() => toggleImage(url)} />
                        <ExternalImage url={url} />
                      </label>
                    ))}
                  </div>
                )}
              </>
            )}
            <button onClick={createFromExternal} disabled={!externalName.trim()}>
              Produkt mit diesen Daten anlegen
            </button>
          </div>
        )}
      </section>

      <h2>Sammelprodukte</h2>
      <div className="card row">
        <input value={newAggregateName} onChange={(e) => setNewAggregateName(e.target.value)} placeholder="Neues Sammelprodukt" />
        <button onClick={createAggregate}>Anlegen</button>
      </div>
      <ul className="list-reset stack">
        {aggregates.map((a) => (
          <li key={a.id} className="card">
            <Link to={`/aggregates/${a.id}`}><strong>{a.name}</strong></Link>
            <div className="muted">{a.products?.length ?? 0} Produkt(e)</div>
          </li>
        ))}
      </ul>

      <h2>Produkte</h2>
      {selected.size > 0 && (
        <div className="card row between" role="toolbar" aria-label="Sammelaktionen">
          <strong>{selected.size} ausgewählt</strong>
          <div className="row">
            <select value={targetListId} onChange={(e) => setTargetListId(e.target.value)} aria-label="Zielliste">
              {openLists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.memoScopeKey ? '📝 Merkliste' : l.name || 'Ohne Namen'}
                </option>
              ))}
            </select>
            <button className="primary" onClick={() => void addSelectedToList()} disabled={!targetListId || moving}>
              {moving ? '…' : 'Auf Liste setzen'}
            </button>
            <input type="date" value={dayDate} onChange={(e) => setDayDate(e.target.value)} aria-label="Einkaufstag" />
            <button onClick={() => void addSelectedToDay()} disabled={!dayDate || moving}>
              Zum Tag
            </button>
            <button onClick={() => setSelected(new Set())} aria-label="Auswahl aufheben" title="Auswahl aufheben">
              ×
            </button>
          </div>
        </div>
      )}
      <ul className="list-reset stack">
        {products.map((p) => (
          <li key={p.id} className="card row between">
            <input
              type="checkbox"
              checked={selected.has(p.id)}
              onChange={() => toggleSelected(p.id)}
              aria-label={`${p.name} auswählen`}
            />
            <div style={{ flex: 1 }}>
              <Link to={`/products/${p.id}`}>
                <strong>{p.name}</strong>
              </Link>
              {p.blocked && <div className="muted">🚫 Gesperrt{p.blockNote ? `: ${p.blockNote}` : ''}</div>}
              {p.brand && <div className="muted">{p.brand}</div>}
              {p.categoryId && categoryNames.get(p.categoryId) && <div className="muted">🏷️ {categoryNames.get(p.categoryId)}</div>}
            </div>
            <Link to={`/products/${p.id}#preise`}>💶 Preise</Link>
          </li>
        ))}
        {products.length === 0 && <p className="muted">Keine Produkte gefunden.</p>}
      </ul>
    </div>
  );
}
